import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Banknote, Scissors, BedDouble, AlertTriangle, ArrowRight } from "lucide-react";
import api, { peso, todayStr } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import { Button } from "../../components/ui/button";

export default function Dashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [xreading, setXreading] = useState(null);
  const [lowStock, setLowStock] = useState([]);
  const [appts, setAppts] = useState([]);
  const [boarders, setBoarders] = useState([]);

  useEffect(() => {
    api.get("/inventory/low-stock").then((r) => setLowStock(r.data)).catch(() => {});
    api.get("/grooming/appointments", { params: { date: todayStr() } }).then((r) => setAppts(r.data)).catch(() => {});
    api.get("/boarding/reservations", { params: { status: "checked_in" } }).then((r) => setBoarders(r.data)).catch(() => {});
    if (["admin", "manager"].includes(user?.role)) {
      api.get("/reports/x-reading").then((r) => setXreading(r.data)).catch(() => {});
    }
  }, [user]);

  const activeAppts = appts.filter((a) => !["cancelled", "no_show"].includes(a.status));

  return (
    <div className="space-y-6 animate-rise">
      <div>
        <h1 className="font-display text-2xl sm:text-3xl font-bold tracking-tight" data-testid="dashboard-title">
          Kumusta, {user?.name?.split(" ")[0]}!
        </h1>
        <p className="text-sm text-slate-500">Here's what's happening at the store today.</p>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="bg-[#0E7C84] text-white rounded-2xl p-5" data-testid="card-today-sales">
          <Banknote className="w-5 h-5 text-[#F4A261] mb-3" />
          <p className="font-display font-bold text-2xl">{xreading ? peso(xreading.gross_sales) : "—"}</p>
          <p className="text-xs text-emerald-100/70">Today's sales {xreading ? `(${xreading.transactions} txn)` : "(managers only)"}</p>
        </div>
        <div className="bg-white rounded-2xl border border-[#BFE8E9] p-5" data-testid="card-appointments">
          <Scissors className="w-5 h-5 text-[#E84855] mb-3" />
          <p className="font-display font-bold text-2xl">{activeAppts.length}</p>
          <p className="text-xs text-slate-500">Grooming appointments today</p>
        </div>
        <div className="bg-white rounded-2xl border border-[#BFE8E9] p-5" data-testid="card-boarders">
          <BedDouble className="w-5 h-5 text-[#2A9D8F] mb-3" />
          <p className="font-display font-bold text-2xl">{boarders.length}</p>
          <p className="text-xs text-slate-500">Pets currently boarding</p>
        </div>
        <div className="bg-white rounded-2xl border border-[#BFE8E9] p-5" data-testid="card-low-stock">
          <AlertTriangle className="w-5 h-5 text-amber-500 mb-3" />
          <p className="font-display font-bold text-2xl">{lowStock.length}</p>
          <p className="text-xs text-slate-500">Low-stock products</p>
        </div>
      </div>

      <div className="grid lg:grid-cols-2 gap-4">
        <div className="bg-white rounded-2xl border border-[#BFE8E9] p-5">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-display font-semibold">Today's grooming schedule</h3>
            {["admin", "manager", "groomer", "cashier"].includes(user?.role) && (
              <Button variant="ghost" size="sm" onClick={() => navigate("/staff/grooming")} data-testid="goto-grooming">Open <ArrowRight className="w-3.5 h-3.5 ml-1" /></Button>
            )}
          </div>
          {activeAppts.length === 0 && <p className="text-sm text-slate-500">No appointments scheduled today.</p>}
          <div className="space-y-2">
            {activeAppts.slice(0, 5).map((a) => (
              <div key={a.id} className="flex items-center justify-between text-sm border-b border-[#E2F5F5] pb-2">
                <span><span className="font-mono font-semibold text-[#0E7C84]">{a.time}</span> — {a.pet_name} ({a.service_name})</span>
                <span className="text-xs text-slate-500 capitalize">{a.status.replace("_", " ")}</span>
              </div>
            ))}
          </div>
        </div>
        <div className="bg-white rounded-2xl border border-[#BFE8E9] p-5">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-display font-semibold">Low-stock alerts</h3>
            {["admin", "manager"].includes(user?.role) && (
              <Button variant="ghost" size="sm" onClick={() => navigate("/staff/inventory")} data-testid="goto-inventory">Open <ArrowRight className="w-3.5 h-3.5 ml-1" /></Button>
            )}
          </div>
          {lowStock.length === 0 && <p className="text-sm text-slate-500">All stocked up.</p>}
          <div className="space-y-2">
            {lowStock.slice(0, 5).map((p) => (
              <div key={p.id} className="flex items-center justify-between text-sm border-b border-[#E2F5F5] pb-2">
                <span>{p.name} <span className="font-mono text-xs text-slate-400">{p.sku}</span></span>
                <span className={`text-xs font-semibold ${p.stock === 0 ? "text-red-600" : "text-amber-600"}`}>{p.stock} left</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
